import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Calendar, Clock, Download, Flag, HardDrive, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage, fileDownloadUrl } from "@/lib/api";
import { formatBytes, formatDateTime, timeUntil } from "@/lib/format";
import { SEO } from "@/components/SEO";
import { FilePreview } from "@/components/FilePreview";
import { FileIcon } from "@/components/FileIcon";
import { ErrorState, statusFromError } from "@/components/ErrorState";
import { ShareLink, shareOrCopy } from "@/features/upload/SuccessCard";
import { useSettings } from "@/hooks/useSettings";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const REASONS = [["malware", "Malware"], ["copyright", "Copyright infringement"], ["illegal", "Illegal content"], ["spam", "Spam"], ["abuse", "Abuse"], ["other", "Other"]];

function ReportDialog({ publicId, open, onOpenChange }) {
  const [reason, setReason] = useState("copyright");
  const [description, setDescription] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/reports", { public_id: publicId, reason, description, reporter_email: email || null });
      toast.success(data.message);
      onOpenChange(false);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="report-dialog">
        <DialogHeader><DialogTitle>Report this file</DialogTitle><DialogDescription>Tell us what is wrong. Reports are reviewed by our team; your details are never shown publicly.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            {REASONS.map(([k, label]) => (
              <button key={k} type="button" onClick={() => setReason(k)} className={`rounded-lg border px-3 py-2 text-left text-sm ${reason === k ? "border-indigo-600 bg-indigo-50 text-indigo-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`} data-testid={`report-reason-${k}`}>{label}</button>
            ))}
          </div>
          <div><Label htmlFor="rd">Details (optional)</Label><Textarea id="rd" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={2000} rows={3} data-testid="report-description" /></div>
          <div><Label htmlFor="re">Your email (optional)</Label><Input id="re" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="report-email" /></div>
          <Button type="submit" disabled={busy} className="w-full rounded-xl bg-indigo-600 hover:bg-indigo-700" data-testid="report-submit">{busy ? "Submitting…" : "Submit report"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function FilePage() {
  const { publicId } = useParams();
  const { settings } = useSettings();
  const [reportOpen, setReportOpen] = useState(false);
  const { data: file, isLoading, error } = useQuery({ queryKey: ["file", publicId], queryFn: async () => (await api.get(`/files/${publicId}`)).data, retry: false });

  if (isLoading) return <div className="container-x max-w-3xl py-10"><Skeleton className="h-8 w-2/3" /><Skeleton className="mt-6 h-72 w-full" /></div>;
  if (error) return <ErrorState status={statusFromError(error)} />;

  const share = () => shareOrCopy(file);
  const meta = [
    { icon: HardDrive, label: "Size", value: formatBytes(file.size), id: "size" },
    { icon: Calendar, label: "Uploaded", value: formatDateTime(file.created_at), id: "uploaded" },
    { icon: Clock, label: "Expires", value: file.expires_at ? formatDateTime(file.expires_at) : "Never", id: "expires" },
  ];
  return (
    <div className="container-x max-w-3xl py-8 sm:py-12" data-testid="file-page">
      <SEO title={file.original_filename} description={`${formatBytes(file.size)} · ${file.mime_type}. Shared via ${settings.site_name}.`} noindex image={file.preview_kind === "image" ? `${process.env.REACT_APP_BACKEND_URL}/api/v1/files/${file.public_id}/preview` : undefined} />
      <div className="card animate-rise p-5 sm:p-8">
        <div className="flex items-start gap-3">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-indigo-50 text-indigo-600"><FileIcon mime={file.mime_type} ext={file.extension} className="h-5 w-5" /></span>
          <div className="min-w-0">
            <h1 className="break-words text-xl font-bold tracking-tight text-slate-900 sm:text-2xl" data-testid="file-name">{file.original_filename}</h1>
            <p className="mt-1 text-xs text-slate-500" data-testid="file-type"><span className="font-mono uppercase">{file.extension || "file"}</span> · {file.mime_type} · <span className="text-slate-400">{timeUntil(file.expires_at)}</span></p>
          </div>
        </div>
        <div className="mt-6"><FilePreview file={file} /></div>
        <dl className="mt-6 grid gap-3 sm:grid-cols-3">
          {meta.map((m) => (
            <div key={m.id} className="flex items-center gap-2.5 rounded-xl border border-slate-100 bg-slate-50/70 px-3 py-2.5" data-testid={`file-meta-${m.id}`}>
              <m.icon className="h-4 w-4 text-slate-400" /><div><dt className="text-[10px] font-medium uppercase tracking-wider text-slate-400">{m.label}</dt><dd className="text-sm font-medium text-slate-800">{m.value}</dd></div>
            </div>
          ))}
        </dl>
        <div className="mt-6 space-y-3">
          <ShareLink url={file.share_url} testId="file-share-link" />
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button asChild className="h-12 flex-1 rounded-xl bg-indigo-600 text-base font-semibold hover:bg-indigo-700"><a href={fileDownloadUrl(file.public_id)} data-testid="file-download-btn"><Download className="mr-2 h-5 w-5" />Download</a></Button>
            <Button variant="outline" className="h-12 rounded-xl" onClick={share} data-testid="file-share-btn"><Share2 className="mr-2 h-4 w-4" />Share</Button>
          </div>
        </div>
        {file.is_dangerous && <p className="mt-4 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="file-danger-notice">This file type cannot be previewed for security reasons. It will download instead.</p>}
        {settings.abuse_reporting_enabled && (
          <div className="mt-6 flex items-center justify-between border-t border-slate-100 pt-4 text-xs text-slate-400">
            <span>{file.download_count} downloads</span>
            <button onClick={() => setReportOpen(true)} className="flex items-center gap-1 hover:text-red-600" data-testid="report-file-btn"><Flag className="h-3.5 w-3.5" />Report this file</button>
          </div>
        )}
      </div>
      <ReportDialog publicId={file.public_id} open={reportOpen} onOpenChange={setReportOpen} />
    </div>
  );
}
